import Axios from "axios";
import { errorMessage } from "@/helper";

const IMAGE_MAX_SIZE = 1 * 1024 * 1024; // 1MB
const IMAGE_ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

// Setup Axios instance with same configuration as in authMiddleware
const axiosInstance = Axios.create({
  baseURL: process.env.API_HOST,
  withCredentials: false,
});

// Add authorization token interceptor (same as authMiddleware)
axiosInstance.interceptors.request.use(
  function (config) {
    if (!config.headers.common || !config.headers.common.hasOwnProperty("Authorization")) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.common = config.headers.common || {};
        config.headers.common["Authorization"] = token;
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

function extensionOf(name) {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

// Client-side pre-check mirroring the backend limits (1MB; jpg/jpeg/png/gif/webp).
// Spares a round trip and gives an instant error; the backend runs the same checks.
function validateImageFile(file) {
  if (file.size > IMAGE_MAX_SIZE) {
    return `File size exceeds ${IMAGE_MAX_SIZE / (1024 * 1024)}MB limit.`;
  }
  if (file.size === 0) {
    return "File is empty";
  }
  if (!IMAGE_ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
    return `Invalid file extension. Allowed: ${IMAGE_ALLOWED_EXTENSIONS.join(", ")}`;
  }
  return null;
}

function escapeHtmlAttr(value) {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function ensureAbsoluteUrl(location) {
  if (location.startsWith("http://") || location.startsWith("https://")) {
    return location;
  }
  const protocol = window.location.protocol;
  const host = window.location.host;
  return protocol + "//" + host + (location.startsWith("/") ? location : "/" + location);
}

function extractImageUrl(json) {
  if (json?.data?.location) {
    return json.data.location;
  }
  if (json?.location) {
    return json.location;
  }
  if (typeof json === "string") {
    return json;
  }
  return null;
}

async function uploadImage(file) {
  const problem = validateImageFile(file);
  if (problem) {
    throw new Error(problem);
  }

  const formData = new FormData();
  formData.append("file", file, file.name);

  try {
    const response = await axiosInstance.post("/notification/upload-image", formData);
    const location = extractImageUrl(response.data);

    if (!location || typeof location !== "string") {
      throw new Error("Invalid response format from server");
    }

    return ensureAbsoluteUrl(location);
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.data ||
        error.response.data?.message ||
        `HTTP Error: ${error.response.status}`;
      throw new Error(message);
    }
    if (error.request) {
      throw new Error("Image upload failed due to network error");
    }
    throw error;
  }
}

// TinyMCE passes pasted and dropped images as blobs (automatic_uploads). Wrap the blob in a File
// so it goes through the same validation and request as the toolbar button.
function createImageUploadHandler() {
  return blobInfo => {
    const blob = blobInfo.blob();
    const file = new File([blob], blobInfo.filename(), { type: blob.type });
    return uploadImage(file);
  };
}

// Plain file input -> validate -> upload -> insertContent. No native `image` plugin/dialog
// involved, so there is nothing to fight with DOM hacks.
function pickAndInsertImage(editor) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/gif,image/webp";
  input.addEventListener("change", () => {
    const file = input.files && input.files[0];
    if (!file) return;

    uploadImage(file).then(
      location => {
        editor.insertContent('<img src="' + escapeHtmlAttr(location) + '" alt="" />');
      },
      error => {
        errorMessage(error.message || "Image upload failed");
      }
    );
  });
  input.click();
}

function setupTinyMCEEventHandlers(editor) {
  editor.ui.registry.addButton("uploadimage", {
    icon: "image",
    tooltip: "Insert image",
    onAction: () => pickAndInsertImage(editor),
  });
}

export { createImageUploadHandler, setupTinyMCEEventHandlers };
